import { HotelInitial, TripLodging, RoomDetails } from "@/types/accommodation";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import api from "@/config/axiosConfig";
import { DateTime } from "luxon";
import logger from "@/utils/logger";
export const ITEMS_PER_PAGE = 20;

interface AgodaHotelsResponse {
  data: HotelInitial[];
  nextCursor: number | null;
}

export const nearbyAgodaHotels = async (
  accommodationBounds: {
    _sw: { lng: number; lat: number };
    _ne: { lng: number; lat: number };
  } | null,
  checkIn: string,
  checkOut: string,
  offset: number = 0,
  currency: string = "USD",
  filters?: {
    minRating?: number;
    priceRange?: string;
  }
): Promise<AgodaHotelsResponse> => {
  if (!accommodationBounds) return { data: [], nextCursor: null };
  try {
    const response = await api.lodgings.get<AgodaHotelsResponse>("/hotels", {
      params: {
        sw_lng: accommodationBounds?._sw.lng,
        sw_lat: accommodationBounds?._sw.lat,
        ne_lng: accommodationBounds?._ne.lng,
        ne_lat: accommodationBounds?._ne.lat,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        offset: offset,
        limit: ITEMS_PER_PAGE,
        currency: currency,
        min_rating: filters?.minRating,
        price_range: filters?.priceRange,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching nearby hotels:", error);
    throw new Error("Failed to fetch nearby hotels");
  }
};

export const useHotels = (
  accommodationBounds: {
    _sw: { lng: number; lat: number };
    _ne: { lng: number; lat: number };
  } | null,
  checkIn: string,
  checkOut: string,
  offset: number,
  currency: string,
  filters?: {
    minRating?: number;
    priceRange?: string;
  }
) => {
  return useQuery({
    queryKey: [
      "nearbyHotels",
      accommodationBounds,
      checkIn,
      checkOut,
      offset,
      currency,
      filters,
    ],
    queryFn: async () => {
      try {
        return await nearbyAgodaHotels(
          accommodationBounds,
          checkIn,
          checkOut,
          offset,
          currency,
          filters
        );
      } catch (error) {
        if (error instanceof Error) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!accommodationBounds && !!checkIn && !!checkOut,
    staleTime: Infinity,
    retry: false,
  });
};

export const useLodgingDetails = (
  type: TripLodging["type"],
  lodgingId: number,
  checkIn: string,
  checkOut: string,
  currency: string
) => {
  return useQuery({
    queryKey: ["lodgingDetails", type, lodgingId, checkIn, checkOut, currency],
    queryFn: () =>
      getLodgingDetails(type, lodgingId, checkIn, checkOut, currency),
    enabled: !!type && !!lodgingId && !!checkIn && !!checkOut && !!currency,
    staleTime: Infinity,
    retry(failureCount, error) {
      if (failureCount >= 3) {
        return false;
      }
      if (error instanceof Error && error.message.includes("404")) {
        return false;
      }
      return true;
    },
  });
};

const getLodgingDetails = async (
  type: TripLodging["type"],
  lodgingId: number,
  checkIn: string,
  checkOut: string,
  currency: string
) => {
  if (type === "custom") {
    return;
  } else if (type === "hotel") {
    return await getHotelDetails(lodgingId, checkIn, checkOut, currency);
  } else {
    return await getHomeStayDetails(lodgingId);
  }
};

export const useHotelDetails = (
  hotelId: number,
  checkIn: string,
  checkOut: string,
  currency: string
) => {
  return useQuery({
    queryKey: ["hotelDetails", hotelId, checkIn, checkOut, currency],
    queryFn: () => getHotelDetails(hotelId, checkIn, checkOut, currency, true),
    enabled: !!hotelId && !!checkIn && !!checkOut && !!currency,
    staleTime: Infinity,
  });
};

export const useRoomDetails = (
  hotelId: number,
  checkIn: string,
  checkOut: string,
  currency: string,
  type: TripLodging["type"]
) => {
  return useQuery({
    queryKey: ["roomDetails", hotelId, checkIn, checkOut, currency],
    queryFn: () => getRoomDetails(hotelId, checkIn, checkOut, currency),
    enabled: !!hotelId && !!checkIn && !!checkOut && type === "hotel",
    staleTime: Infinity,
    retry: false,
  });
};

export const getRoomDetails = async (
  hotelId: number,
  checkIn: string,
  checkOut: string,
  currency: string = "USD"
): Promise<RoomDetails[]> => {
  try {
    const response = await api.lodgings.get<RoomDetails[]>("/rooms", {
      params: {
        hotel_id: hotelId,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        currency: currency,
      },
    });
    if (Array.isArray(response.data) && response.data.length === 0) {
      throw response.data;
    }
    return response.data;
  } catch (error) {
    throw new Error("Failed to fetch room details");
  }
};

export const getHotelDetails = async (
  hotelId: number,
  checkIn: string,
  checkOut: string,
  currency: string = "USD",
  moreDetails: boolean = false
) => {
  try {
    const response = await api.lodgings.get("/hotels", {
      params: {
        hotel_id: hotelId,
        more_details: moreDetails,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        currency: currency,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching hotel details:", error);
    throw new Error("Failed to fetch hotel details");
  }
};

export const getBookingLink = async (
  hotelId: number,
  checkIn: string,
  checkOut: string,
  currency: string = "USD"
): Promise<string> => {
  try {
    const response = await api.lodgings.get("/hotels", {
      params: {
        hotel_id: hotelId,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        currency: currency,
        more_details: true,
      },
    });
    return response.data.url;
  } catch (error) {
    console.error("Error fetching booking link:", error);
    throw new Error("Failed to fetch booking link");
  }
};

const getHomeStayDetails = async (homeStayId: number) => {
  try {
    const response = await api.lodgings.get("/home-stays", {
      params: {
        listing_id: homeStayId,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching home stay details:", error);
    throw new Error("Failed to fetch home stay details");
  }
};

export const getHotelImages = async (hotelId: number) => {
  try {
    const response = await api.lodgings.get("/hotels", {
      params: {
        hotel_id: hotelId,
        more_details: true,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching hotel images:", error);
    throw new Error("Failed to fetch hotel images");
  }
};

const increaseImageResolution = async (imageUrls: string[]) => {
  return imageUrls.map((url) => url.replace(/s=312x/, "s=800"));
};

function parseTimeString(timeString: string): {
  hours: number;
  minutes: number;
  seconds: number;
} {
  const timeRegex = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?\s*$/i;
  const match = timeString.match(timeRegex);

  if (!match) {
    throw new Error(`Invalid time string: "${timeString}"`);
  }

  const [, hourStr = "", minuteStr = "", secondStr = "", ampmStr = ""] = match;

  let hours = parseInt(hourStr, 10);
  const minutes = parseInt(minuteStr, 10);
  const seconds = secondStr ? parseInt(secondStr, 10) : 0;
  const ampm = ampmStr.toUpperCase() || undefined;

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
    throw new Error(`Invalid time components in time string: "${timeString}"`);
  }

  if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) {
    throw new Error(
      `Invalid minutes or seconds in time string: "${timeString}"`
    );
  }

  if (ampm) {
    if (hours < 1 || hours > 12) {
      throw new Error(`Invalid hours in time string: "${timeString}"`);
    }
    if (ampm === "AM" && hours === 12) {
      hours = 0;
    } else if (ampm === "PM" && hours < 12) {
      hours += 12;
    }
  } else {
    if (hours < 0 || hours > 23) {
      throw new Error(`Invalid hours in time string: "${timeString}"`);
    }
  }

  return { hours, minutes, seconds };
}

const combineDateAndTime = (
  date: string,
  timeString: string,
  timezone: string
): string => {
  logger.log(
    `Combining date: ${date} and time: ${timeString} in timezone: ${timezone}`
  );

  const timeParts = DateTime.fromFormat(timeString, "HH:mm").toFormat("HH:mm");
  logger.log(`Parsed time:`, timeParts);

  const dt = DateTime.fromFormat(
    `${date.split("T")[0]} ${timeParts}`,
    "yyyy-MM-dd HH:mm",
    { zone: timezone }
  );
  logger.log(`DateTime in specified timezone:`, dt.toString());

  const result = dt.toUTC().toISO();
  logger.log(`Resulting UTC ISO string:`, result);

  return result || "";
};

export const convertHotelIntoTripLodging = async (
  hotel: HotelInitial,
  checkIn: string,
  checkOut: string,
  timezone: string,
  numNights: number,
  selectedRoom?: RoomDetails
) => {
  const tripLodging: TripLodging = {
    id: hotel.hotelId,
    name: hotel.hotelName,
    type: "hotel",
    description: "This is a test description",
    policies: {
      checkIn: combineDateAndTime(checkIn, hotel.policies.checkIn, timezone),
      checkOut: combineDateAndTime(checkOut, hotel.policies.checkOut, timezone),
    },
    duration: numNights,
    pricing: {
      amount: selectedRoom
        ? selectedRoom.price.total / numNights
        : hotel.dailyRate,
      total: selectedRoom
        ? selectedRoom.price.total
        : hotel.dailyRate * numNights,
      currency: hotel.currency,
    },
    coordinates: {
      latitude: hotel.latitude,
      longitude: hotel.longitude,
    },
    mainImageURL: hotel.imageURL,
    address: hotel.addressline1 || "",
    moreInfo: {
      reviewCount: hotel.reviewCount,
      reviewScore: hotel.reviewScore,
      roomName: selectedRoom?.name,
      roomId: selectedRoom?.id,
    },
    bookingStatus: "not_booked",
  };

  return tripLodging;
};
